import { DraftBadge } from '@/components/badges/Common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import { useAuth } from '@/db/useAuth';
import { pendingBrandRequestCountState, spacesState } from '@/state';
import { sortSpacesDraftsLast } from '@/lib/spaceOrder';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { LogOut } from 'lucide-react';
import { type FC, type PropsWithChildren, type ReactNode, useMemo } from 'react';
import { Link, NavLink, type To, useMatch } from 'react-router-dom';

type NavChild = {
    /** Stable key, unique within its section. */
    key: string;
    to: string;
    label: ReactNode;
};

/**
 * The admin's only navigation: top-level sections, each of which reveals its own sub-pages while
 * it's the active one. Spaces are sub-pages like any other section's — the space you're editing is
 * one click away from the space next to it, and there's no separate place to go looking for it.
 */
const LeftNav: FC = () => {
    const { user, logout } = useAuth();
    const spaces = useAsyncAtomValue(spacesState, []);
    const pendingBrandRequests = useAsyncAtomValue(pendingBrandRequestCountState, undefined);

    const matchDashboard = useMatch('/');
    const matchDirectory = useMatch('/directory/*');
    const matchSpaces = useMatch('/spaces/*');
    const matchProducts = useMatch('/products/*');
    const matchEquipment = useMatch('/equipment/*');
    const matchContent = useMatch('/content/*');
    const matchSystem = useMatch('/system/*');

    // Creating a space lives on the Spaces list, with the other tables' Add buttons — the nav lists
    // what exists.
    const spaceChildren: NavChild[] = useMemo(
        () =>
            sortSpacesDraftsLast(spaces).map((space) => ({
                key: space.id,
                to: `/spaces/${space.id}`,
                label: (
                    <span className="flex items-center justify-between gap-2">
                        {space.name}
                        {!space.published && <DraftBadge />}
                    </span>
                ),
            })),
        [spaces],
    );

    const items: Array<{
        label: string;
        to: string;
        isActive: boolean;
        children?: NavChild[];
    }> = [
        {
            label: 'Dashboard',
            to: '/',
            isActive: !!matchDashboard,
        },
        {
            label: 'Directory',
            to: '/directory/homes',
            isActive: !!matchDirectory,
            children: [
                { key: 'homes', label: 'Homes', to: '/directory/homes' },
                { key: 'users', label: 'Users', to: '/directory/users' },
                { key: 'companies', label: 'Companies', to: '/directory/companies' },
            ],
        },
        {
            label: 'Spaces',
            to: '/spaces',
            isActive: !!matchSpaces,
            children: spaceChildren,
        },
        {
            label: 'Products',
            to: '/products/models',
            isActive: !!matchProducts,
            children: [
                { key: 'models', label: 'Models', to: '/products/models' },
                { key: 'documents', label: 'Documents', to: '/products/documents' },
                { key: 'brands', label: 'Brands', to: '/products/brands' },
                {
                    key: 'brand-requests',
                    label: <CountedLabel count={pendingBrandRequests}>Brand Requests</CountedLabel>,
                    to: '/products/brand-requests',
                },
                { key: 'shared-nameplates', label: 'Shared Nameplates', to: '/products/shared-nameplates' },
                { key: 'recalls', label: 'Recalls', to: '/products/recalls' },
            ],
        },
        {
            label: 'Equipment',
            to: '/equipment/types',
            isActive: !!matchEquipment,
            children: [
                { key: 'types', label: 'Equipment Types', to: '/equipment/types' },
                { key: 'default-guide', label: 'Default Guide', to: '/equipment/default-guide' },
                { key: 'library', label: 'Test Library', to: '/equipment/library' },
                { key: 'core-fields', label: 'Core Fields', to: '/equipment/core-fields' },
            ],
        },
        {
            label: 'Content',
            to: '/content/considerations',
            isActive: !!matchContent,
            children: [
                { key: 'considerations', label: 'Considerations', to: '/content/considerations' },
                { key: 'tasks', label: 'Tasks', to: '/content/tasks' },
                { key: 'lessons', label: 'Lessons', to: '/content/lessons' },
                { key: 'default-task-screen', label: 'Default Task Screen', to: '/content/default-task-screen' },
            ],
        },
        {
            label: 'System',
            to: '/system/status',
            isActive: !!matchSystem,
            children: [
                { key: 'status', label: 'Status', to: '/system/status' },
                { key: 'jobs', label: 'Jobs', to: '/system/jobs' },
                { key: 'domain-events', label: 'Domain Events', to: '/system/domain-events' },
            ],
        },
    ];

    return (
        <div className="flex h-full flex-col gap-6 p-4">
            {user && (
                <div className="flex items-center gap-2 border-b pb-4">
                    <UserAvatar imageUrl={user.photoURL} name={user.displayName ?? user.email ?? ''} />
                    <div className="min-w-0 flex-1 truncate text-sm font-medium">{user.email}</div>
                </div>
            )}
            <div>
                <h3 className="text-base font-semibold">Rock Admin</h3>
            </div>
            <div className="flex flex-col gap-2">
                {items.map(({ label, to, isActive, children }) => (
                    <div key={label} className="flex flex-col gap-2">
                        <Button
                            asChild
                            variant={isActive ? 'default' : 'outline'}
                            className="justify-start"
                            aria-current={isActive ? 'page' : undefined}
                        >
                            <Link to={to}>{label}</Link>
                        </Button>
                        {children && isActive && (
                            <div className="flex flex-col items-stretch my-1">
                                {children.map((child) => (
                                    <PageLink key={child.key} to={child.to}>
                                        {child.label}
                                    </PageLink>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
                <Button variant="outline" className="justify-start" onClick={logout}>
                    <LogOut className="h-4 w-4" /> Sign Out
                </Button>
            </div>
        </div>
    );
};

/** A sub-page label with how much is waiting there — shown only when something is. */
const CountedLabel: FC<PropsWithChildren<{ count: number | undefined }>> = ({ count, children }) => (
    <span className="flex items-center justify-between gap-2">
        {children}
        {!!count && <Badge variant="default">{count}</Badge>}
    </span>
);

const PageLink: FC<PropsWithChildren<{ to: To; className?: string }>> = ({ to, className = '', children }) => (
    <NavLink
        to={to}
        className={({ isActive }) =>
            `text-xs py-2 px-5 rounded transition ${isActive ? `bg-slate-200 font-medium` : `hover:bg-slate-200`} ${className}`
        }
    >
        {children}
    </NavLink>
);

export default LeftNav;
